import { createSelector } from 'reselect';
import { RootState } from '../../../../types';
import { EntityTypes } from '../../../../constants/entities';
import { IEntityReducer } from '../../../reducers/entities';
import {
  ActionStyle,
  ActionType,
  IAction,
  IActionBasic,
  IActionDAO,
  IActionEntity,
  IActionEntityBasic,
  isBubble,
  isContain,
  isEnd,
  isHighlight,
  isOverlay,
  isStart,
} from 'types/domain/action';
import { IModelEntityConfig } from 'types/domain/model';
import { IMediaAsset } from 'types/domain/media';
import { ISceneConfig } from 'types/domain/scene';
import { IModuleOut } from 'types/domain/module';
import { getEntities } from 'core/selectors/utils/getEntities';
import { parseActionEntityBasic } from './actionEntity';
import { parseSceneDAO } from '../scene/scene';
import { IKeyArray } from 'types/IKeyArray';
import { IUploadFile } from 'core/slices/upload';
import { parseVariableDAO } from 'core/selectors/admin/variable';
import { createCachedSelector } from 're-reselect';
import { getYouTubeVideoId } from 'utils/youtube';
import { actionChannel } from 'redux-saga/effects';

const entity = (state: RootState) => state.entity.present;
const id = (state: RootState, id: number) => id || state.state[EntityTypes.ACTION];
const uploads = (state: RootState) => state.core.upload.uploads;

export const selectAction = createCachedSelector(
  entity,
  id,
  uploads,
  parseAction,
)((state, id) => id || state.state[EntityTypes.ACTION] || 'unknown');
export const selectActionDAO = createCachedSelector(
  entity,
  id,
  uploads,
  parseActionDAO,
)((state, id) => id || state.state[EntityTypes.ACTION] || 'unknown');

export function parseAction(entity: IEntityReducer, id: number, uploads?: IKeyArray<IUploadFile>) {
  if (id === null) return null;

  const action: IAction = Object.values(entity[EntityTypes.ACTION]).find(a => [a.id, a.offlineId].includes(id));

  if (!action) return null;

  const entities: IActionEntityBasic[] =
    Object.values(entity[EntityTypes.ACTION_ENTITY])
      .filter((ent: IActionEntity) => ent.actionId === id && ent.status > -1)
      .map(ent => parseActionEntityBasic(entity, ent.id)) || [];

  const scene = action.sceneId ? entity[EntityTypes.SCENE][action.sceneId] : null;
  const voiceScene = action.voiceSceneId ? entity[EntityTypes.SCENE][action.voiceSceneId] : null;
  return action
    ? ({
        ...action,
        sceneConfigId: action.sceneConfigId || scene?.sceneConfigId,
        voiceSceneConfigId: action.voiceSceneConfigId || voiceScene?.sceneConfigId,
        entities,
        isDecision: entities.filter(e => e && e.correct).length === 1,
        isMultichoice: entities.filter(e => e && e.correct).length > 1,
        outs: getEntities(entity, EntityTypes.MODULE_OUT, 'sourceId', id)
          .filter(
            (out: IModuleOut) =>
              entity[EntityTypes.ACTION][out.targetId] && entity[EntityTypes.ACTION][out.targetId].status > -1,
          )
          .reduce((prev, next: IModuleOut) => {
            return { ...prev, [`${next.sourceHandle}`]: next };
          }, {}),
        text: action.textId ? entity[EntityTypes.TEXT][action.textId] : null,
        node: action.nodeId ? entity[EntityTypes.NODE][action.nodeId] : null,
        scene,
        name: action.name || entity[EntityTypes.SCENE][action.sceneId]?.name,
        variable: action.variableId ? entity[EntityTypes.VARIABLE][action.variableId] : null,
        sceneConfig:
          action.sceneConfigId != null
            ? entity[EntityTypes.SCENE_CONFIG][action.sceneConfigId]
            : scene
            ? entity[EntityTypes.SCENE_CONFIG][scene.sceneConfigId]
            : null,
        voiceScene,
        voiceSceneConfig:
          action.voiceSceneConfigId != null
            ? entity[EntityTypes.SCENE_CONFIG][action.voiceSceneConfigId]
            : voiceScene
            ? entity[EntityTypes.SCENE_CONFIG][voiceScene.sceneConfigId]
            : null,
        youTubeId: getYouTubeVideoId(action?.objectUrl),
      } as IActionBasic)
    : null;
}

export function parseActionDAO(entity: IEntityReducer, id: number, uploads?: IKeyArray<IUploadFile>) {
  if (id === null) return null;

  const action: IAction = Object.values(entity[EntityTypes.ACTION]).find(a => [a.id, a.offlineId].includes(id));
  const entities: IActionEntityBasic[] =
    Object.values(entity[EntityTypes.ACTION_ENTITY])
      .filter((ent: IActionEntity) => ent.actionId === id && ent.status > -1)
      .map(ent => parseActionEntityBasic(entity, ent.id)) || [];

  return action
    ? ({
        ...action,
        text: action.textId ? entity[EntityTypes.TEXT][action.textId] : null,
        entities,
        isDecision: entities.filter(e => e && e.status > -1).length > 0,
        isMultichoice: entities.filter(e => e && e.correct).length >= 1,
        node: action.nodeId ? entity[EntityTypes.NODE][action.nodeId] : null,
        modelConfigs: Object.values(entity[EntityTypes.MODEL_ENTITY_CONFIG]).filter(
          (mec: IModelEntityConfig) => mec.actionId === id,
        ),

        scene: action.sceneId ? parseSceneDAO(entity, action.sceneId) : null,
        voiceScene: action.voiceSceneId ? parseSceneDAO(entity, action.voiceSceneId) : null,
        variable: action.variableId ? parseVariableDAO(entity, action.variableId) : null,
        mediaAsset: action.mediaAssetId ? (entity[EntityTypes.MEDIA_ASSET][action.mediaAssetId] as IMediaAsset) : null,
        sceneConfig: action.sceneConfigId
          ? (entity[EntityTypes.SCENE_CONFIG][action.sceneConfigId] as ISceneConfig)
          : null,
        voiceSceneConfig: action.voiceSceneConfigId
          ? (entity[EntityTypes.SCENE_CONFIG][action.voiceSceneConfigId] as ISceneConfig)
          : null,
        outs: getEntities(entity, EntityTypes.MODULE_OUT, 'sourceId', id)
          .filter(
            (out: IModuleOut) =>
              entity[EntityTypes.ACTION][out.targetId] && entity[EntityTypes.ACTION][out.targetId].status > -1,
          )
          .reduce((prev, next: IModuleOut) => {
            return { ...prev, [`${next.sourceHandle}`]: next };
          }, {}),
        upload: uploads
          ? Object.values(uploads).find((up: IUploadFile) => up.parentType === EntityTypes.ACTION && up.parentId === id)
          : null,
        youTubeId: getYouTubeVideoId(action?.objectUrl),
      } as IActionDAO)
    : null;
}

export const selectActionType = createSelector([entity, id], (entity, id) => entity[EntityTypes.ACTION][id]?.type);
export const selectActionSceneId = createSelector(
  [entity, id],
  (entity, id) => entity[EntityTypes.ACTION][id]?.sceneId,
);

export const selectActionVoiceSceneId = createSelector(
  [entity, id],
  (entity, id) => entity[EntityTypes.ACTION][id]?.voiceSceneId,
);

export const selectActionStyle = createSelector([entity, id], (entity, id) => entity[EntityTypes.ACTION][id]?.style);
export const selectIsActionDark = createSelector(
  [entity, id],
  (entity, id) => entity[EntityTypes.ACTION][id]?.style === ActionStyle.DARK,
);

export const selectActionYouTubeId = createSelector([entity, id], (entity, id) =>
  getYouTubeVideoId(entity[EntityTypes.ACTION][id]?.objectUrl),
);

export const selectIsActionDarkBg = createSelector([entity, id], (entity, id) => {
  const action = entity[EntityTypes.ACTION][id];

  if (!action) return false;

  return (
    getYouTubeVideoId(action.objectUrl) ||
    action.style === ActionStyle.DARK ||
    [ActionType.INTERACTIVE].includes(action.type) ||
    isHighlight(action.type)
  );
});

export const selectIsActionImage = createSelector([entity, id], (entity, id) => {
  const action = entity[EntityTypes.ACTION][id];

  if (!action) return false;

  return (
    getYouTubeVideoId(action.objectUrl) ||
    [ActionType.INTERACTIVE].includes(action.type) ||
    isHighlight(action.type) ||
    isOverlay(action.type) ||
    ([ActionType.SIMPLE].includes(action.type) && !!action.sceneId)
  );
});

export const selectIsActionTopImage = createSelector([entity, id], (entity, id) => {
  const action = entity[EntityTypes.ACTION][id];

  if (!action) return false;

  return isStart(action.type) && !isBubble(action.type) && !!action.sceneId;
});

export const selectIsActionBottomImage = createSelector([entity, id], (entity, id) => {
  const action = entity[EntityTypes.ACTION][id];

  if (!action) return false;

  return isStart(action.type) && !isBubble(action.type) && !!action.sceneId;
});
